import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useState } from "react";

import { IconButton } from "../../../../ui";
import { getThumbnailDimensions } from "../utils";

const SortableThumbnail = ({
  item,
  index,
  thumbnailSize,
  isSelected,
  onSelect,
  onUnselect,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.archiveId });

  // Aspect ratio (naturalWidth / naturalHeight), detected once the image loads
  // so portrait images render portrait and landscape render landscape.
  const [ratio, setRatio] = useState(null);

  const { width, height } = getThumbnailDimensions(thumbnailSize, ratio);

  const handleImageLoad = event => {
    const { naturalWidth, naturalHeight } = event.target;
    if (naturalWidth && naturalHeight) {
      setRatio(naturalWidth / naturalHeight);
    }
  };

  const style = {
    width: `${width}px`,
    // Translate only — deliberately drop dnd-kit's scaleX/scaleY. With variable
    // width thumbnails the sorting strategy would otherwise scale the dragged
    // item to fit the slot it hovers, stretching a portrait into a landscape
    // (or vice-versa). Translating instead keeps the dragged item its own shape
    // and lets the opening slot animate to the dragged item's width.
    transform: CSS.Translate.toString(transform),
    // Keep dnd-kit's transform animation, but also ease width when the scale
    // slider changes (dnd-kit only sets `transition` while sorting).
    transition: [transition, "width 200ms ease"].filter(Boolean).join(", "),
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex flex-col"
      {...attributes}
      {...listeners}
    >
      <div
        style={{ height: `${height}px` }}
        className="group relative border-2 bg-gray-100 transition-[height,border-color] duration-200 ease-in-out hover:cursor-move hover:border-black"
      >
        <img
          src={item.imgT}
          alt={`Artwork ${item.archiveId}`}
          onLoad={handleImageLoad}
          className="h-full w-full object-contain"
        />
        <div
          className={`invisible
             ${isSelected && "!visible"}
              absolute left-0 top-0 flex h-full w-full justify-center bg-black/[.5] group-hover:visible`}
        >
          {isSelected ? (
            <IconButton variant="clean" title="Deselect">
              <XMarkIcon
                className="h-8 w-8 cursor-pointer rounded-lg bg-white p-2"
                onClick={() => onUnselect(item.archiveId)}
              />
            </IconButton>
          ) : (
            <IconButton variant="clean" title="Select">
              <PlusIcon
                className="h-8 w-8 cursor-pointer rounded-lg bg-white p-2"
                onClick={() => onSelect(item.archiveId)}
              />
            </IconButton>
          )}
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <div className="px-1 text-sm font-bold tracking-wide text-gray-600">
          {index + 1}
        </div>
        <div className="px-1 text-sm tracking-wide text-gray-600">
          {item.archiveId}
        </div>
      </div>
    </div>
  );
};

export default SortableThumbnail;
